// La gráfica del informe (```grafica). SVG propio, sin librería: barras (verticales u horizontales) y líneas, un solo eje,
// rejilla discreta, leyenda con 2+ series, el valor al pasar el ratón (o al enfocar con el teclado) y la tabla de datos
// plegada debajo para quien prefiera los números.
import { useState } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { SERIES, UNA_SERIE, type Grafica as G } from './bloques';

const fmt = (v: number, unidad?: string) => {
  const n = Math.abs(v) >= 1000 ? v.toLocaleString('es-ES', { maximumFractionDigits: 0 }) : v.toLocaleString('es-ES', { maximumFractionDigits: 2 });
  return unidad ? `${n} ${unidad}` : n;
};

/** Marcas «redondas» del eje: 0 … máximo en 4 pasos. */
function escala(max: number, min: number): number[] {
  const bajo = Math.min(0, min);
  const rango = max - bajo || 1;
  const bruto = rango / 4;
  const mag = 10 ** Math.floor(Math.log10(bruto));
  const paso = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((p) => p >= bruto) ?? bruto;
  const desde = Math.floor(bajo / paso) * paso;
  const marcas: number[] = [];
  for (let v = desde; v <= max + paso * 0.001; v += paso) marcas.push(Math.round(v * 1e6) / 1e6);
  if (marcas[marcas.length - 1]! < max) marcas.push(marcas[marcas.length - 1]! + paso);
  return marcas;
}

export function Grafica({ g }: { g: G }) {
  const [foco, setFoco] = useState<{ s: number; i: number } | null>(null);
  const colores = g.series.length === 1 ? [UNA_SERIE] : SERIES;
  const todos = g.series.flatMap((s) => s.valores);
  const marcas = escala(Math.max(...todos), Math.min(...todos));
  const vMin = marcas[0]!;
  const vMax = marcas[marcas.length - 1]!;
  const horizontal = g.tipo === 'barras-horizontales';

  const W = 720;
  const izq = horizontal ? Math.min(220, 16 + 7.2 * Math.max(...g.etiquetas.map((e) => e.length))) : 56;
  const der = horizontal ? 48 : 16;
  const arriba = horizontal ? 12 : 24;
  const n = g.etiquetas.length;
  const fila = 34;
  const H = horizontal ? arriba + n * fila + 28 : 280;
  const abajo = horizontal ? 28 : 40;
  const ancho = W - izq - der;
  const alto = H - arriba - abajo;
  const pos = (v: number) => (horizontal ? izq + ((v - vMin) / (vMax - vMin)) * ancho : arriba + alto - ((v - vMin) / (vMax - vMin)) * alto);
  const cero = pos(Math.max(vMin, 0));
  const banda = (horizontal ? alto : ancho) / n;
  const grueso = Math.min(horizontal ? 22 : 44, (banda * 0.7) / (g.tipo === 'lineas' ? 1 : g.series.length));
  const centro = (i: number) => (horizontal ? arriba : izq) + banda * i + banda / 2;

  const enFoco = foco ? { etiqueta: g.etiquetas[foco.i]!, serie: g.series[foco.s]!.nombre, valor: g.series[foco.s]!.valores[foco.i]! } : null;

  return (
    <figure className="my-6 rounded-o-l border border-oficina-linea bg-oficina-panel p-5">
      {g.titulo ? <figcaption className="mb-1 text-o-cuerpo font-semibold text-oficina-texto">{g.titulo}</figcaption> : null}
      <div className="mb-2 flex min-h-6 flex-wrap items-center gap-x-4 gap-y-1 text-o-sub text-oficina-texto2">
        {g.series.length > 1
          ? g.series.map((s, k) => (
              <span key={s.nombre} className="inline-flex items-center gap-1.5">
                <span className={cn('h-2.5 w-2.5 rounded-sm', colores[k]!.fondo)} aria-hidden />
                {s.nombre}
              </span>
            ))
          : null}
        <span className="ml-auto tabular-nums text-oficina-texto" aria-live="polite">
          {enFoco ? `${enFoco.etiqueta}${g.series.length > 1 ? ` · ${enFoco.serie}` : ''}: ${fmt(enFoco.valor, g.unidad)}` : ' '}
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="block h-auto w-full" role="img" aria-label={g.titulo ?? t('oficinaV2.informe.grafica')} onMouseLeave={() => setFoco(null)}>
        {marcas.map((m) => (
          <g key={m}>
            {horizontal ? (
              <>
                <line x1={pos(m)} x2={pos(m)} y1={arriba} y2={arriba + alto} strokeWidth={1} className="stroke-oficina-linea" />
                <text x={pos(m)} y={H - 8} textAnchor="middle" fontSize={12} className="fill-oficina-texto3">
                  {fmt(m, g.unidad)}
                </text>
              </>
            ) : (
              <>
                <line x1={izq} x2={W - der} y1={pos(m)} y2={pos(m)} strokeWidth={1} className="stroke-oficina-linea" />
                <text x={izq - 8} y={pos(m) + 4} textAnchor="end" fontSize={12} className="fill-oficina-texto3">
                  {fmt(m, g.unidad)}
                </text>
              </>
            )}
          </g>
        ))}
        {g.etiquetas.map((e, i) =>
          horizontal ? (
            <text key={e + i} x={izq - 10} y={centro(i) + 4} textAnchor="end" fontSize={13} className="fill-oficina-texto2">
              {e}
            </text>
          ) : (
            <text key={e + i} x={centro(i)} y={H - 14} textAnchor="middle" fontSize={13} className="fill-oficina-texto2">
              {e}
            </text>
          ),
        )}
        {g.tipo === 'lineas'
          ? g.series.map((s, k) => {
              const puntos = s.valores.map((v, i) => `${centro(i)},${pos(v)}`).join(' ');
              return (
                <g key={s.nombre}>
                  <polyline points={puntos} fill="none" className={colores[k]!.trazo} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                  {s.valores.map((v, i) => (
                    <circle
                      key={i}
                      cx={centro(i)}
                      cy={pos(v)}
                      r={foco?.s === k && foco.i === i ? 6 : 4}
                      strokeWidth={2}
                      tabIndex={0}
                      onMouseEnter={() => setFoco({ s: k, i })}
                      onFocus={() => setFoco({ s: k, i })}
                      onBlur={() => setFoco(null)}
                      className={cn('stroke-oficina-panel outline-none', colores[k]!.relleno)}
                    />
                  ))}
                </g>
              );
            })
          : g.series.map((s, k) =>
              s.valores.map((v, i) => {
                const desplaz = (k - (g.series.length - 1) / 2) * (grueso + 2);
                const a = pos(v);
                const p0 = Math.min(a, cero);
                const largo = Math.max(1, Math.abs(a - cero));
                const atenuada = foco && !(foco.s === k && foco.i === i);
                const r = Math.min(4, grueso / 2);
                return (
                  <rect
                    key={`${k}-${i}`}
                    x={horizontal ? p0 : centro(i) + desplaz - grueso / 2}
                    y={horizontal ? centro(i) + desplaz - grueso / 2 : p0}
                    width={horizontal ? largo : grueso}
                    height={horizontal ? grueso : largo}
                    rx={r}
                    opacity={atenuada ? 0.45 : 1}
                    tabIndex={0}
                    onMouseEnter={() => setFoco({ s: k, i })}
                    onFocus={() => setFoco({ s: k, i })}
                    onBlur={() => setFoco(null)}
                    className={cn('outline-none transition-opacity', colores[k]!.relleno)}
                  >
                    <title>{`${g.etiquetas[i]}${g.series.length > 1 ? ` · ${s.nombre}` : ''}: ${fmt(v, g.unidad)}`}</title>
                  </rect>
                );
              }),
            )}
        {g.tipo !== 'lineas' && g.series.length === 1 && n <= 12
          ? g.series[0]!.valores.map((v, i) => (
              <text
                key={`v${i}`}
                x={horizontal ? pos(v) + (v >= 0 ? 8 : -8) : centro(i)}
                y={horizontal ? centro(i) + 4 : pos(v) + (v >= 0 ? -8 : 16)}
                textAnchor={horizontal ? (v >= 0 ? 'start' : 'end') : 'middle'}
                fontSize={13}
                fontWeight={600}
                className="pointer-events-none fill-oficina-texto tabular-nums"
              >
                {fmt(v)}
              </text>
            ))
          : null}
        {horizontal ? <line x1={cero} x2={cero} y1={arriba} y2={arriba + alto} className="stroke-oficina-texto3" /> : <line x1={izq} x2={W - der} y1={cero} y2={cero} className="stroke-oficina-texto3" />}
      </svg>
      <details className="mt-2 text-o-sub text-oficina-texto3">
        <summary className="cursor-pointer select-none hover:text-oficina-texto2">{t('oficinaV2.informe.verDatos')}</summary>
        <table className="mt-2 w-full text-left tabular-nums">
          <thead>
            <tr>
              <th className="py-1 pr-4 font-medium" />
              {g.series.map((s) => (
                <th key={s.nombre} className="py-1 pr-4 font-medium text-oficina-texto2">
                  {s.nombre}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {g.etiquetas.map((e, i) => (
              <tr key={e + i} className="border-t border-oficina-linea">
                <td className="py-1 pr-4 text-oficina-texto2">{e}</td>
                {g.series.map((s) => (
                  <td key={s.nombre} className="py-1 pr-4 text-oficina-texto">
                    {fmt(s.valores[i]!, g.unidad)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  );
}
